import OutlinePrimaryButton from "components/buttons/outline-primary-button";
import StripeSetupButton from "components/Dashboard/DoctorDashboard/components/stripe-setup-button";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import ModalSectionLabel from "components/modal/modal-section-label";
import messages from "messages";
import React, { ComponentPropsWithoutRef, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import styles from "./styles.module.scss";
import { ReactComponent as OutlineNewscreen } from "assets/svg/outline-newscreen.svg";
import { useSchedule } from "components/Dashboard/DoctorDashboard/contexts/ScheduleContext";
import { useSessionTypes } from "contexts/SessionTypesContext";
import Checkbox from "components/checkbox/Checkbox";
import { AmplitudePageName, track } from "utils/amplitude";

type Props = ComponentPropsWithoutRef<typeof ModalLayout> &
  Partial<{
    // hit the close button
    onHide: () => void;
    // hit the apply (should call onHide inside component)
    onApply: () => void;
    // hit the cancel (should call onHide inside component)
    onCancel: () => void;
  }> & { amplitudePageName: AmplitudePageName };

const AMPLITUDE_COMPONENT_NAME = "stripe-modal";

const StripeModal = ({
  onHide = () => null,
  onCancel = () => null,
  onApply = () => null,
  show,
  amplitudePageName,
  ...props
}: Props) => {
  const { forceReload: reloadSchedule } = useSchedule();
  const { forceReload: reloadSessionTypes } = useSessionTypes();
  const [checked, setChecked] = useState(false);

  const handleApply = useCallback(() => {
    reloadSchedule();
    reloadSessionTypes();
    return onApply();
  }, [onApply, reloadSchedule, reloadSessionTypes]);

  return (
    <ModalLayout
      show={show}
      onHide={onHide}
      Header={messages.setupPaymentMethod}
      Footer={
        <>
          <OutlinePrimaryButton
            onClick={() => {
              track(
                amplitudePageName,
                [AMPLITUDE_COMPONENT_NAME, "skip-and-do-later-button"],
                "click",
              );
              handleApply();
            }}
          >
            {messages.skipAndDoLater}
          </OutlinePrimaryButton>
        </>
      }
      {...props}
      bodyClass={`px-modal ${styles["stripe-modal__body"]}`}
    >
      <ModalSectionLabel title={messages.setupPaymentMethod} />
      <p className="rg-12 gray01">{messages.setupPaymentMethodInfo}</p>
      <StripeSetupButton
        disabled={!checked}
        variant="outline-primary"
        amplitudePageName={amplitudePageName}
        amplitudeComponentName={AMPLITUDE_COMPONENT_NAME}
        children={
          <>
            <OutlineNewscreen className={"mr-1"} />
            {messages.setup}
          </>
        }
        className={styles["stripe-modal__connect-btn"]}
      />
      <Checkbox
        checked={checked}
        onChange={(e) => setChecked(e.target.checked)}
        onClick={() => {
          track(
            amplitudePageName,
            [
              AMPLITUDE_COMPONENT_NAME,
              "independent-contractor-agreement-checbox",
            ],
            "click",
          );
        }}
        id="independent-contractor-agreement"
        label={
          <label>
            {messages.iHaveRead}
            <Link
              to="/independent-contractor-agreement"
              target="_blank"
              rel="noopener noreferrer"
            >
              {messages.independentContractorAgreement}
            </Link>
            <span className={styles["stripe-modal__required"]}>{"*"}</span>
          </label>
        }
      />
    </ModalLayout>
  );
};

export default StripeModal;
